import React from 'react';
import { Braces, ArrowUpRight, Check, Activity } from 'lucide-react';
import SignalOrb from './SignalOrb';
import { heroStats } from '../../mock';

/**
 * Dedicated Signal Orb section — sits below the minimal hero.
 * Editorial layout: rich narrative copy left, live orb + KPIs right.
 */
export default function SignalOrbSection() {
  return (
    <section id="signal" className="relative py-20 lg:py-32 bg-[#0F1C40] text-[#F1EDE4] border-t border-white/5 overflow-hidden">
      <div
        className="absolute top-1/4 left-1/2 w-[600px] h-[600px] -translate-x-1/2 rounded-full opacity-30 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(78,205,196,0.35), transparent 70%)' }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-center">
          {/* Left: narrative */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.3em] text-[#4ECDC4] mb-5">
              <Activity className="w-3.5 h-3.5" />
              <span>The Signal Orb</span>
            </div>

            <h2 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[1.02] font-medium">
              Every buyer leaves a{' '}
              <span className="font-serif italic font-normal text-[#4ECDC4]">signal.</span>{' '}
              We turn it into{' '}
              <span className="font-serif italic font-normal text-[#FFB020]">pipeline.</span>
            </h2>

            <p className="mt-8 text-[15px] lg:text-lg text-white/70 leading-relaxed max-w-2xl">
              At the core of everything we build sits the{' '}
              <span className="text-[#F1EDE4] font-medium">Signal Orb</span> — a continuously-inferring
              intent engine that streams 15+ live signal types across 46M+ enterprise contacts. Funding
              rounds, executive hires, tech installs, content engagement, ad interactions, hiring surges,
              site visits — each one weighted, scored, and routed in under two seconds.
            </p>
            <p className="mt-4 text-[15px] lg:text-lg text-white/60 leading-relaxed max-w-2xl">
              It&apos;s not a report. It&apos;s not a dashboard. It&apos;s a{' '}
              <span className="text-[#4ECDC4] italic">live nervous system</span> for your revenue
              team — the reason a great EvolveBPM lead card tells your reps exactly who to call first,
              why now, and what will land.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-xl">
              {[
                { title: 'First-party intent', desc: 'Owned publisher network + Predictor.com — not modelled guesses.' },
                { title: 'Streaming inference', desc: '~1.8s per lead. Signals ingested, ranked, routed in real time.' },
                { title: 'Buying-committee resolution', desc: 'CIO, VP, Director-tier — every persona surfaced together.' },
                { title: 'Continuous re-scoring', desc: 'Cooling accounts drop. Newly active ones rise. Automatically.' },
              ].map((f) => (
                <div key={f.title} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg bg-[#4ECDC4]/15 border border-[#4ECDC4]/30 flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5 text-[#4ECDC4]" strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="text-[13px] font-medium text-[#F1EDE4]">{f.title}</div>
                    <div className="text-[12px] text-white/55 leading-snug mt-0.5">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-[#4ECDC4] text-[#0F1C40] pl-5 pr-4 py-3 rounded-full text-sm font-semibold hover:bg-[#FFB020] transition-colors"
              >
                See it on your ICP
                <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
              </a>
              <a
                href="/predictor"
                className="inline-flex items-center gap-2 border border-white/25 text-[#F1EDE4] pl-5 pr-4 py-3 rounded-full text-sm hover:border-[#4ECDC4] hover:text-[#4ECDC4] transition-colors"
              >
                Explore Predictor.com
              </a>
            </div>
          </div>

          {/* Right: live orb card */}
          <div className="lg:col-span-5">
            <div className="relative bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl p-6 lg:p-8 overflow-hidden">
              <div
                className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(78,205,196,0.4), transparent 70%)' }}
              />
              <div
                className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(255,176,32,0.3), transparent 70%)' }}
              />
              <div className="relative">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4]">
                    <Braces className="w-3.5 h-3.5" />
                    <span>Signal Orb · live core</span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-white/70">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] pulse-dot" />
                    <span>Inferring</span>
                  </div>
                </div>

                <div className="relative h-64 lg:h-72 rounded-xl overflow-hidden border border-white/10">
                  <SignalOrb />
                  <div className="absolute bottom-3 left-3 flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-white/85 bg-[#0F1C40]/70 backdrop-blur px-2.5 py-1 rounded-full border border-white/15">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] pulse-dot" />
                    <span>15+ signals</span>
                  </div>
                  <div className="absolute top-3 right-3 text-[10px] font-mono uppercase tracking-widest text-[#00E5FF] bg-[#0F1C40]/70 backdrop-blur px-2.5 py-1 rounded-full border border-[#00E5FF]/40">
                    46M contacts
                  </div>
                </div>

                <div className="mt-6 pt-5 border-t border-white/10 grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-serif italic text-[#4ECDC4] tabular">98%</div>
                    <div className="text-[9px] font-mono uppercase text-white/50 mt-0.5">Accuracy</div>
                  </div>
                  <div>
                    <div className="text-2xl font-serif italic text-[#FFB020] tabular">1.8s</div>
                    <div className="text-[9px] font-mono uppercase text-white/50 mt-0.5">Per lead</div>
                  </div>
                  <div>
                    <div className="text-2xl font-serif italic text-[#4ECDC4] tabular">15+</div>
                    <div className="text-[9px] font-mono uppercase text-white/50 mt-0.5">Signal types</div>
                  </div>
                </div>
              </div>
            </div>

            {/* KPI strip below orb */}
            <div className="mt-4 grid grid-cols-2 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
              {heroStats.slice(0, 4).map((s, i) => (
                <div key={i} className="bg-[#0F1C40] px-4 py-4">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-1.5">
                    {s.label}
                  </div>
                  <div className="text-2xl lg:text-3xl font-serif italic text-[#F1EDE4] tabular leading-none">
                    {s.value}
                  </div>
                  <div className="text-[10px] text-white/50 mt-1.5">{s.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
